import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import translations, { DEFAULT_LOCALE, SUPPORTED_LOCALES } from './locales';

const STORAGE_KEY = 'vital_locale';
const I18nContext = createContext(null);

const supportedCodes = SUPPORTED_LOCALES.map(l => l.code);

const detectInitialLocale = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && supportedCodes.includes(stored)) return stored;
  } catch (e) { /* ignore */ }
  try {
    const nav = (navigator.language || navigator.userLanguage || '').toLowerCase();
    if (nav.startsWith('pt')) return 'pt-BR';
    if (nav.startsWith('en')) return 'en';
    if (nav.startsWith('es')) return 'es';
  } catch (e) { /* ignore */ }
  return DEFAULT_LOCALE;
};

/** Resolve dotted key like "hero.subtitle" from a translation object. */
const resolveKey = (dict, key) => {
  if (!dict || !key) return undefined;
  const parts = key.split('.');
  let cur = dict;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
};

export const I18nProvider = ({ children }) => {
  const [locale, setLocaleState] = useState(detectInitialLocale);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, locale); } catch (e) { /* ignore */ }
    try { document.documentElement.lang = locale; } catch (e) { /* ignore */ }
  }, [locale]);

  const setLocale = useCallback((next) => {
    if (supportedCodes.includes(next)) setLocaleState(next);
  }, []);

  const t = useCallback((key, fallback) => {
    const dict = translations[locale] || translations[DEFAULT_LOCALE];
    const value = resolveKey(dict, key);
    if (value !== undefined) return value;
    // Fallback to default locale
    const defaultValue = resolveKey(translations[DEFAULT_LOCALE], key);
    if (defaultValue !== undefined) return defaultValue;
    return fallback ?? key;
  }, [locale]);

  const value = useMemo(() => ({
    locale, setLocale, t, locales: SUPPORTED_LOCALES,
  }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useTranslation = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Safe fallback when used outside provider (e.g. tests/storybook)
    return {
      t: (key, fallback) => fallback ?? key,
      locale: DEFAULT_LOCALE,
      setLocale: () => {},
      locales: SUPPORTED_LOCALES,
    };
  }
  return ctx;
};
